import { create } from "zustand";
import { QuotesWatchlistStore, WatchlistItem } from "./types";
import { getStoredSymbolKeys, saveSymbolKeys, getStoredSymbolDigits, saveSymbolDigits } from "./storage";
import { DEFAULT_QUOTES_WATCHLIST } from "./default-data";
import { TABLET_BREAKPOINT } from "@/hooks/use-mobile";
import { CHART_PANEL_IDS } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import type { Symbol } from "@/vybe-trader-client/symbols/types";
import { useJournalStore } from "@/vybe-trader-client/messaging/stores/useJournalStore";

/**
 * The saved watchlist, ready to render, read straight from localStorage.
 *
 * Used for the store's INITIAL state, not just by loadWatchlist/resetWatchlist, because both of those are
 * reached through effects that do not run at mount: useQuotesWatchlistSync only resets when the account KEY
 * changes, and generateAccountKey returns null until the accounts store holds both an active id and the
 * matching account — so the first non-null key arrives with the connection. Measured on prod: rows appeared
 * at 2,342ms, right behind the SignalR connect at ~2,204ms, for symbols already on disk before the page
 * loaded. Seeding the store itself puts them on the first paint instead.
 */
function watchlistFromStorage(): { savedSymbolKeys: string[]; watchlist: WatchlistItem[] } {
  try {
    const savedSymbolKeys = getStoredSymbolKeys();
    const digits = getStoredSymbolDigits();
    return {
      savedSymbolKeys,
      watchlist: savedSymbolKeys.map((symbolKey) => ({
        symbolKey,
        digits: digits[symbolKey] ?? 5,
      })),
    };
  } catch {
    // Never let a storage problem stop the app booting — the catalog sync repopulates either way.
    return { savedSymbolKeys: [], watchlist: [] };
  }
}

export const useQuotesWatchlistStore = create<QuotesWatchlistStore>(
  (set, get) => ({
    ...watchlistFromStorage(),

    addToWatchlist: async (symbol: Symbol) => {
      const currentWatchlist = get().watchlist;
      const savedKeys = get().savedSymbolKeys;
      const wasWatchlistEmpty = currentWatchlist.length === 0;

      // Check if symbol already exists in watchlist
      const existsInWatchlist = currentWatchlist.some(
        (watchlistItem) => watchlistItem.symbolKey === symbol.currency
      );

      if (!existsInWatchlist) {
        const newItem: WatchlistItem = {
          symbolKey: symbol.currency,
          digits: symbol.digits,
        };

        const updatedWatchlist = [...currentWatchlist, newItem];

        useJournalStore.getState().addEntry({
          level: "info",
          category: "watchlist",
          message: `Added ${symbol.currency} to quotes watchlist`,
          detail: { symbol: symbol.currency },
          sanitizeDetail: false,
        });

        // Update store
        set({ watchlist: updatedWatchlist });

        // If watchlist was empty, set this symbol as selected in chart store
        if (wasWatchlistEmpty) {
          try {
            const { useChartStore } = await import(
              "@/vybe-trader-client/chart/stores/useChartStore"
            );
            useChartStore.getState().selectSymbol(symbol);
          } catch (error) {
            console.error(
              `[QuotesWatchlist] Failed to update selected symbol:`,
              error
            );
          }
        }
      }

      // Check if symbol already exists in saved keys
      if (!savedKeys.includes(symbol.currency)) {
        const updatedKeys = [...savedKeys, symbol.currency];
        set({ savedSymbolKeys: updatedKeys });

        // Save to localStorage
        saveSymbolKeys(updatedKeys);
      }

      // Update subscribed symbols store
      try {
        const { useSubscribedSymbolsStore } = await import(
          "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore"
        );
        useSubscribedSymbolsStore.getState().subscribe(symbol);
      } catch (error) {
        console.error(
          `[QuotesWatchlist] Failed to add ${symbol.currency} to subscribed symbols:`,
          error
        );
      }
    },

    removeFromWatchlist: async (symbol: Symbol) => {
      const currentWatchlist = get().watchlist;
      const savedKeys = get().savedSymbolKeys;

      // First check if symbol can be unsubscribed
      try {
        const { useSubscribedSymbolsStore } = await import(
          "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore"
        );
        const subscribedStore = useSubscribedSymbolsStore.getState();

        // Check if symbol has open trades
        const subscribedSymbol = subscribedStore.getSubscribedSymbol(
          symbol.currency
        );
        if (
          subscribedSymbol &&
          (subscribedSymbol.hasOpenBuy || subscribedSymbol.hasOpenSell)
        ) {
          console.warn(
            `[QuotesWatchlist] Cannot remove ${symbol.currency} from watchlist - has open trades`
          );
          // Return early without removing from watchlist
          return;
        }

        // Symbol can be removed - proceed with removal
        // Remove from watchlist
        const updatedWatchlist = currentWatchlist.filter(
          (item) => item.symbolKey !== symbol.currency
        );
        set({ watchlist: updatedWatchlist });

        // Check if removed symbol was selected in chart store
        import("@/vybe-trader-client/chart/stores/useChartStore").then(
          ({ useChartStore }) => {
            const chartStore = useChartStore.getState();
            const selectedSymbol = chartStore.selectedSymbol;

            if (selectedSymbol && selectedSymbol.currency === symbol.currency) {
              // Removed symbol was selected, choose replacement
              if (updatedWatchlist.length > 0) {
                // Get symbols store to find the first remaining symbol object
                import(
                  "@/vybe-trader-client/symbols/stores/useSymbolsStore"
                ).then(({ useSymbolsStore }) => {
                  const symbolsStore = useSymbolsStore.getState();
                  const firstRemainingSymbolKey = updatedWatchlist[0].symbolKey;
                  const firstRemainingSymbol = symbolsStore.byCurrency.get(
                    firstRemainingSymbolKey
                  );

                  if (firstRemainingSymbol) {
                    chartStore.selectSymbol(firstRemainingSymbol);
                  } else {
                    chartStore.selectSymbol(null);
                  }
                });
              } else {
                // No symbols left, clear selection

                chartStore.selectSymbol(null);
              }
            }
          }
        );

        // Remove from saved keys
        const updatedKeys = savedKeys.filter((key) => key !== symbol.currency);
        set({ savedSymbolKeys: updatedKeys });

        // Save updated keys to localStorage
        saveSymbolKeys(updatedKeys);

        useJournalStore.getState().addEntry({
          level: "info",
          category: "watchlist",
          message: `Removed ${symbol.currency} from quotes watchlist`,
          detail: { symbol: symbol.currency },
          sanitizeDetail: false,
        });

        // Now unsubscribe from subscribed symbols store
        subscribedStore.unsubscribe(symbol.currency);
      } catch (error) {
        console.error(
          `[QuotesWatchlist] Failed to remove ${symbol.currency} from watchlist:`,
          error
        );
      }
    },

    loadWatchlist: () => {
      // Load symbol keys from localStorage
      const symbolKeys = getStoredSymbolKeys();

      // Render the saved rows NOW, from the digits cached alongside the keys. This used to set an empty
      // watchlist and wait for the symbol catalog, which streams in batches — so a refresh showed
      // "No symbols in watchlist" until every one of the account's symbols had arrived, ~15s on a large
      // account, even though the eight rows the user actually watches were known before the page loaded.
      // syncWithAvailableSymbols corrects digits and drops anything the account does not offer as soon as
      // the catalog is authoritative.
      void symbolKeys;
      set(watchlistFromStorage());
    },

    syncWithAvailableSymbols: (
      availableSymbols: Map<string, Symbol>,
      catalogComplete: boolean = true
    ) => {
      const savedKeys = get().savedSymbolKeys;
      const cachedDigits = { ...getStoredSymbolDigits() };
      const syncedWatchlist: WatchlistItem[] = [];

      // Map saved symbol keys to actual symbols, preserving the order from savedKeys.
      //
      // While the catalog is still streaming its batches this map is INCOMPLETE, so a symbol missing from
      // it means "not loaded yet", not "this account does not offer it". Dropping such a row here would
      // make the watchlist flicker rows out and back in as batches land, so a partial catalog only ever
      // upgrades a row (cached digits → real digits) and never removes one. Only the complete catalog is
      // authoritative enough to remove.
      const previous = get().watchlist;
      savedKeys.forEach((symbolKey) => {
        const symbol = availableSymbols.get(symbolKey);
        if (symbol) {
          cachedDigits[symbol.currency] = symbol.digits;
          syncedWatchlist.push({
            symbolKey: symbol.currency,
            digits: symbol.digits,
          });
          return;
        }
        if (!catalogComplete) {
          const held = previous.find((item) => item.symbolKey === symbolKey);
          if (held) syncedWatchlist.push(held);
          else syncedWatchlist.push({ symbolKey, digits: cachedDigits[symbolKey] ?? 5 });
        }
      });

      // Keep the render cache fresh so the NEXT refresh renders these rows with correct digits at once.
      saveSymbolDigits(cachedDigits);

      // Logging into an account with an empty watchlist: seed the defaults
      // (only those the account actually offers) instead of showing nothing.
      // Requires the COMPLETE catalog — an empty result from a half-streamed one means "not arrived yet",
      // and seeding on that would write defaults into localStorage for an account that never asked for them.
      if (syncedWatchlist.length === 0 && catalogComplete) {
        const seededKeys: string[] = [];
        DEFAULT_QUOTES_WATCHLIST.forEach((item) => {
          const symbol = availableSymbols.get(item.symbolKey);
          if (symbol) {
            syncedWatchlist.push({
              symbolKey: symbol.currency,
              digits: symbol.digits,
            });
            seededKeys.push(symbol.currency);
          }
        });

        if (seededKeys.length > 0) {
          // Default chart view for a first login: desktop gets one seeded
          // symbol per chart panel (chart 1 → EURUSD … chart 4 → USDCAD);
          // the mobile shell only uses slot 0, so it just gets EURUSD.
          const isDesktopViewport =
            typeof window !== "undefined" &&
            window.innerWidth >= TABLET_BREAKPOINT;
          const seededSymbols = syncedWatchlist
            .map((item) => availableSymbols.get(item.symbolKey))
            .filter((symbol): symbol is Symbol => Boolean(symbol));
          const chartSymbols = isDesktopViewport
            ? seededSymbols.slice(0, CHART_PANEL_IDS.length)
            : seededSymbols.slice(0, 1);
          import(
            "@/vybe-trader-client/chart/stores/useChartStore3"
          ).then(({ useChartStore3 }) => {
            chartSymbols.forEach((symbol, index) => {
              useChartStore3
                .getState()
                .setSelectedSymbol(symbol, CHART_PANEL_IDS[index]);
            });
          });

          // Merge into saved keys (keys for other accounts are preserved)
          const updatedKeys = [
            ...seededKeys.filter((key) => !savedKeys.includes(key)),
            ...savedKeys,
          ];
          set({ savedSymbolKeys: updatedKeys });
          saveSymbolKeys(updatedKeys);

          useJournalStore.getState().addEntry({
            level: "info",
            category: "watchlist",
            message: `Seeded default watchlist: ${seededKeys.join(", ")}`,
            detail: { symbols: seededKeys },
            sanitizeDetail: false,
          });
        }
      }

      // Update store with synced watchlist
      set({ watchlist: syncedWatchlist });

      // IMPORTANT: Do NOT update savedSymbolKeys or localStorage here
      // We keep all symbols in localStorage even if not available for current account
      // Only update localStorage on explicit add/remove actions by user

      // Initialize subscribed symbols with the synced symbols
      if (syncedWatchlist.length > 0) {
        // Get Symbol objects for synced watchlist
        const symbolsToInitialize: Symbol[] = [];
        syncedWatchlist.forEach((item) => {
          const symbol = availableSymbols.get(item.symbolKey);
          if (symbol) {
            symbolsToInitialize.push(symbol);
          }
        });

        // Initialize and subscribe all symbols
        import(
          "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore"
        ).then(({ useSubscribedSymbolsStore }) => {
          useSubscribedSymbolsStore
            .getState()
            .initializeAndSubscribe(symbolsToInitialize);
        });

        // Check and update selected symbol in chart store
        import("@/vybe-trader-client/chart/stores/useChartStore").then(
          ({ useChartStore }) => {
            const chartStore = useChartStore.getState();
            const selectedSymbol = chartStore.selectedSymbol;

            // Check if current selected symbol is in the synced watchlist
            const isSelectedSymbolAvailable =
              selectedSymbol &&
              syncedWatchlist.some(
                (item) => item.symbolKey === selectedSymbol.currency
              );

            if (!isSelectedSymbolAvailable) {
              // Selected symbol not available, set to first available symbol
              const firstSymbolKey = syncedWatchlist[0].symbolKey;
              const firstSymbol = availableSymbols.get(firstSymbolKey);
              if (firstSymbol) {
                chartStore.selectSymbol(firstSymbol);
              }
            } else {
            }
          }
        );
      }
    },

    resetWatchlist: () => {
      // Keep saved keys from localStorage and render them straight away.
      //
      // This — not loadWatchlist — is the watchlist's real entry point on a refresh:
      // useQuotesWatchlistSync calls it from the account-key effect, which fires on mount as the key goes
      // from null to the active account. (initializeQuotesWatchlistStore, which calls loadWatchlist, has no
      // caller outside the README.) Clearing to [] here is what put "No symbols in watchlist" on screen for
      // the whole time the symbol catalog was streaming, and it would have undone loadWatchlist's hydration
      // even if something did call it. Measured on prod: rows appeared at 7.5s.
      //
      // The rows are reconciled by syncWithAvailableSymbols the moment symbols arrive, so showing the saved
      // ones now costs nothing and is correct even across an account switch — the saved key list is global.
      set(watchlistFromStorage());
    },

    handleTradeSymbol: async (
      symbol: Symbol,
      hasBuy: boolean,
      hasSell: boolean
    ) => {
      const currentWatchlist = get().watchlist;
      const savedKeys = get().savedSymbolKeys;
      const isInWatchlist = currentWatchlist.some(
        (item) => item.symbolKey === symbol.currency
      );

      // If symbol is not in watchlist but has trades, add it
      if (!isInWatchlist && (hasBuy || hasSell)) {
        // Add to watchlist
        const newItem: WatchlistItem = {
          symbolKey: symbol.currency,
          digits: symbol.digits,
        };

        const updatedWatchlist = [...currentWatchlist, newItem];
        set({ watchlist: updatedWatchlist });

        // Also add to saved keys if not already there
        if (!savedKeys.includes(symbol.currency)) {
          const updatedKeys = [...savedKeys, symbol.currency];
          set({ savedSymbolKeys: updatedKeys });
          saveSymbolKeys(updatedKeys);
        }
      }

      try {
        const { useSubscribedSymbolsStore } = await import(
          "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore"
        );
        const subscribedStore = useSubscribedSymbolsStore.getState();

        // Always update or add to subscribed symbols if there are trades
        if (hasBuy || hasSell) {
          const isSubscribed = subscribedStore.isSubscribed(symbol.currency);

          if (isSubscribed) {
            // Already subscribed, just update trade status
            subscribedStore.updateTradeStatus(symbol.currency, hasBuy, hasSell);
          } else {
            // Not subscribed, add as trade symbol
            subscribedStore.addSymbolForTrade(symbol, hasBuy || hasSell);
            // Then update with correct buy/sell status
            subscribedStore.updateTradeStatus(symbol.currency, hasBuy, hasSell);
          }
        } else if (isInWatchlist) {
          // Symbol is in watchlist but no trades, just update status
          subscribedStore.updateTradeStatus(symbol.currency, false, false);
        }
      } catch (error) {
        console.error(
          `[QuotesWatchlist] Failed to handle trade symbol ${symbol.currency}:`,
          error
        );
      }
    },

    updateWatchlistOrder: (newOrder: string[]) => {
      const currentSavedKeys = get().savedSymbolKeys;
      const currentWatchlist = get().watchlist;

      // Create a set of symbols in new order for O(1) lookup
      const newOrderSet = new Set(newOrder);

      // Build the new savedSymbolKeys array:
      // 1. Start with the new order
      const updatedKeys = [...newOrder];

      // 2. Add any symbols from savedSymbolKeys that aren't in the watchlist
      // (these are symbols saved for other accounts)
      currentSavedKeys.forEach((key) => {
        if (!newOrderSet.has(key)) {
          updatedKeys.push(key);
        }
      });

      // Update savedSymbolKeys
      set({ savedSymbolKeys: updatedKeys });

      // Save to localStorage
      saveSymbolKeys(updatedKeys);

      // Also update the watchlist order to match
      const orderedWatchlist: WatchlistItem[] = [];
      const watchlistMap = new Map<string, WatchlistItem>();

      // Create map for O(1) lookup
      currentWatchlist.forEach((item) => {
        watchlistMap.set(item.symbolKey, item);
      });

      // Build ordered watchlist based on new order
      newOrder.forEach((symbolKey) => {
        const item = watchlistMap.get(symbolKey);
        if (item) {
          orderedWatchlist.push(item);
        }
      });

      // Update watchlist with new order
      set({ watchlist: orderedWatchlist });
    },
  })
);
